"use client";

import { useCallback, useId, useMemo, useState, type ReactNode } from "react";
import { useCountInBeats } from "@/hooks/journey/use-count-in-beats";
import { useRhythmRun } from "@/hooks/journey/use-rhythm-run";
import {
  useHandsOnControls,
  useHandsOnPiano,
} from "@/hooks/journey/use-hands-on-controls";
import { useRunKeyboardShortcuts } from "@/hooks/journey/use-run-keyboard-shortcuts";
import { useTrace } from "@/hooks/journey/use-trace";
import { TraceBand } from "../shell/trace-band";
import { useStudio } from "@/contexts/studio-context";
import type { LearningJourney } from "@/lib/learning-journey/journey";
import { repertoire as repertoireStore } from "@/lib/learning-journey/repertoire";
import type { RangeRunner } from "@/lib/learning-journey/runtime/range-runner";
import type { Phrase, PhraseId, RunRange } from "@/lib/learning-journey/types";
import type { AnalysisSection } from "@/lib/music/analyzer-types";
import type { SongControler } from "@/lib/track/controler/song-controler";
import type { PracticeGroup } from "../practice";
import {
  actDuration,
  heldThrough,
  pieceRate,
  pieceSections,
  readAgo,
  readSections,
  restoreSections,
  reworkLabel,
  sectionName,
  spanSeconds,
  toRework,
  traversalLine,
  weakestCellTick,
  type SectionState,
  KEPT_ACT_SECONDS,
  LONG_ACT_SECONDS,
  type PieceSection,
  type ReworkTarget,
} from "../piece";
import {
  barOfTick,
  bpmAt,
  honouredRatio,
  playedClean,
  pulseLine,
  rhythmGroups,
  stepRate,
  strayLine,
  RATE_LADDER,
  type RunReading,
} from "../rhythm";
import { FloatingHeader } from "../shell/floating-header";
import { JOURNEY_HINT_COLOR_STRAY } from "@/hooks/journey/journey-hint-mapping";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import type { Hint } from "@/contexts/hint-context";
import { HandsOnHint } from "../shell/hands-on-hint";
import { FloatingShell } from "../shell/floating-shell";
import {
  OFFER_CLASS,
  OFFER_INLINE_STYLE,
  OFFER_STYLE,
  PANEL_PADDING_X_PX,
  PANEL_WIDTH_PX,
  TRACE_EDGE,
  TRACE_INK,
  TRACE_MARK,
} from "../shell/panel-metrics";
import { PieceRail } from "../shell/piece-rail";
import { UnitInTime } from "./rhythm-state";

/**
 * View for **phase 2**: the piece as a whole.
 *
 * ## What it stopped being
 *
 * Three fixed tiers — 75 / 90 / 100 % — one button playing the **whole piece**
 * end to end, a list with one line per work unit, and a criterion inherited
 * from S4. Measured, each of those was wrong at this scale:
 *
 * - a piece is **531 attacks** in the median against 12 for a unit, and a
 *   traversal at the tempo it actually holds runs **6 min 12** (p90 13 min
 *   59). Only **19.0% of pieces (38 / 200)** cross in three minutes;
 * - **70.3% of units (7 406 / 10 530) open below 0.75**, so the lowest tier
 *   sat *above* where phase 1 leaves almost everyone, and the real climb is
 *   **9 rungs of `RATE_LADDER`** — 49 BPM to 106 — not two;
 * - the written tempo is exactly 120, the MuseScore default, in **67.1% of
 *   PDMX**, so "100% of the target tempo" referred two thirds of the time to
 *   a number nobody chose;
 * - the list held **38 lines in the median** (p90 96, max 483), more than ten
 *   in 91.5% of pieces, below the fold of a panel already overflowing by 212
 *   px — and what it opened was a stripped-down S4;
 * - and the criterion never consulted a pitch, so an entirely wrong
 *   performance validated the phase, and the journey with it.
 *
 * ## What it is
 *
 * One tempo for the piece — the spec is right about that, and it is what
 * being a piece means — starting at what the piece holds **everywhere** and
 * climbing one rung at a time. One object, chosen on the rail. One act
 * repeated: **the work**, two to four passages, each opening the real S4
 * screen rather than a copy of it. And one traversal, which is not the
 * training loop but the **instrument**: on 531 attacks the share answered is
 * measured to ±1.3 points, where a unit of 12 gives ±8.7.
 *
 * ## Why there is no stepper
 *
 * In phase 1 the progression is behind the player — five steps crossed inside
 * a unit — so it lives in the footer, under the action. Here it is in front
 * of them, so it lives in the body: **the rail is what replaced the
 * stepper**. Counting five steps of a unit would be a 46 px lie.
 */

const LEAD_BOX_H_PX = 34;

/** Lines of work the panel may show. Four is what the widest state affords. */
const MAX_REWORK_LINES = 4;

type SpeedupStateProps = {
  readonly journey: LearningJourney;
  readonly rangeRunner: RangeRunner | null;
  readonly songControler: SongControler | null;
  readonly phrases: readonly Phrase[];
  readonly sections: readonly AnalysisSection[];
  readonly bpm: number;
  readonly ppq: number;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
  /**
   * The piece has been played through once and is now kept rather than
   * climbed. Same screen: what changes is that nothing is a gate any more,
   * and the long act is allowed to be longer.
   */
  readonly repertoire: boolean;
  /**
   * What the repertoire needs to offer this piece back later. `null` when
   * the piece has no catalogue identity, i.e. when nothing could open it.
   */
  readonly keep: {
    readonly morceauId: string;
    readonly pieceId: string;
    readonly title: string;
  } | null;
};

/** One slot in the hint layer; re-publishing replaces it in place. */
const STRAY_HINT_SOURCE = "journey-piece-stray";

export function SpeedupState({
  journey,
  rangeRunner,
  songControler,
  phrases,
  sections,
  bpm,
  ppq,
  ticksPerBeat,
  ticksPerBar,
  repertoire,
  keep,
}: SpeedupStateProps) {
  const titleId = useId();
  const { studio } = useStudio();
  // Read once, in a lazy initialiser: the age of a traversal is counted in
  // days, so re-reading the clock on every render would buy nothing and make
  // the render impure.
  const [mountedAt] = useState(() => Date.now());

  const cells = useMemo(() => pieceSections(phrases, sections, ticksPerBar), [phrases, sections, ticksPerBar]);

  // ── The piece's tempo ────────────────────────────────────────

  const openingRate = useMemo(
    () =>
      journey.getSnapshot().tempoSpeedupCourant ??
      pieceRate(phrases, (i) => journey.unitRate(i), bpm, ppq) ??
      RATE_LADDER[0],
    [journey, phrases, bpm, ppq],
  );
  const [rate, setRate] = useState(openingRate);
  const effectiveBpm = bpmAt(bpm, rate);

  const handleStepTempo = useCallback(
    (direction: -1 | 1) => {
      const next = stepRate(rate, direction);
      setRate(next);
      journey.setPieceRate(next);
    },
    [rate, journey],
  );

  // ── The object of the act ────────────────────────────────────

  const [selected, setSelected] = useState<string | null>(null);
  // Set on the traversal that carried the piece into the repertoire, so the
  // one sentence that marks it is said once and never again.
  const [justEntered, setJustEntered] = useState(false);
  // Offered at the top of the ladder, taken by the player, never taken back.
  const [withMetronome, setWithMetronome] = useState(true);
  const [working, setWorking] = useState<number | null>(null);

  const selectedCell = useMemo(() => cells.find((c) => c.id === selected) ?? null, [cells, selected]);
  const range = useMemo(() => spanRange(phrases, selectedCell), [phrases, selectedCell]);
  const groups = useMemo(() => (range === null ? EMPTY_GROUPS : rhythmGroups(range)), [range]);
  const seconds = range === null ? 0 : spanSeconds(range.startTick, range.endTick, ppq, bpm, rate);
  const covered = useMemo(() => selectedCell?.unitIndices ?? phrases.map((_, i) => i), [selectedCell, phrases]);

  // ── The pass ─────────────────────────────────────────────────

  const [lastPass, setLastPass] = useState<LastPass | null>(null);

  const handleFinished = useCallback(
    (finished: { reading: RunReading; rate: number }) => {
      setLastPass({
        reading: finished.reading,
        groups,
        // Now, not later: the lane is emptied by the next pass, and by the
        // time anything renders the count would be a promise about a lane
        // that no longer holds what it counted.
        strikes: studio?.liveStrikeCount() ?? 0,
      });

      const through = heldThrough(finished.reading, honouredRatio(finished.reading));
      const whole = selectedCell === null;

      // Only a crossing of the whole piece is remembered as the map: a part
      // played alone says nothing about the parts it did not touch.
      //
      // The journey is written **first**, and the repertoire last. Both can
      // fail, but only one of them holds the six minutes that were just
      // played: a repertoire write that threw before this point would cost
      // the map of the traversal and every unit tempo it earned.
      if (whole) {
        journey.recordTraversal(
          readSections(cells, groups, finished.reading).map((state, i) => ({
            id: cells[i].id,
            honoured: state.honoured ?? 0,
            stopped: state.stopped,
          })),
          finished.rate,
          through,
        );
      }

      // A traversal that held is evidence for every unit it crossed: the
      // piece was played through at this tempo, so none of those passages is
      // "slower than the piece" any more.
      //
      // **Under the right keys**, which `heldThrough` does not ask and phase 1
      // has always asked (`landedPass`). What is written here is the tempo
      // each of those units is remembered by, and it is read back cold, one
      // rung down, the next time the player opens any of them
      // (`unitOpeningRate`). A traversal played beside the notes is not
      // evidence that a unit holds — it is exactly the pass phase 2 already
      // refuses to hand the *piece's* next rung to, three lines of screen
      // away (`held && !clean`). Withholding one and writing the other is the
      // same claim answered twice, differently.
      //
      // Deliberately not folded into `heldThrough`: the traversal happened,
      // the rail says so, and the repertoire still takes the piece. Only what
      // is *inferred about the units* is withheld.
      if (through && playedClean(finished.reading)) {
        for (const index of covered) {
          journey.recordUnitRate(finished.rate, index);
        }
      }

      // The piece is played through: it stops being climbed and starts being
      // kept. On the reading the screen already computes, at whatever tempo
      // the player is on — never "at the written tempo", which is the
      // MuseScore default two thirds of the time.
      if (through && whole && !repertoire) {
        setJustEntered(true);
        journey.enterRepertoire();
      }

      if (!through || !whole || keep === null) {
        return;
      }

      // The duration the offer will quote, measured on **this** run rather
      // than on the component's current rate: it describes what was just
      // played, and it is the only number that line carries.
      const played = range === null ? 0 : spanSeconds(range.startTick, range.endTick, ppq, bpm, finished.rate);

      if (repertoire) {
        // Already kept, and crossed again: each recall buys a longer silence
        // before the piece is offered back. The ladder has no visual home —
        // naming it would turn keeping a piece into a schedule to keep up
        // with, which is the one thing the repertoire is meant not to be.
        repertoireStore().playedThrough(keep.morceauId, Date.now(), played);

        return;
      }

      repertoireStore().remember({ ...keep, seconds: played }, Date.now());
    },
    [groups, covered, journey, cells, selectedCell, repertoire, keep, range, ppq, bpm, studio],
  );

  const run = useRhythmRun({
    journey,
    rangeRunner,
    range: range ?? EMPTY_RANGE,
    groups,
    bpm,
    ppq,
    ticksPerBar,
    rate,
    state: "phase2",
    auditionSong: false,
    accompanyHand: null,
    metronome: withMetronome,
    songControler,
    onFinished: handleFinished,
  });

  const reading = lastPass?.reading ?? null;
  const honoured = reading === null ? 1 : honouredRatio(reading);
  const heldNow = heldThrough(reading, honoured);
  // No reading is not "dirty" — and neither is a traversal held yesterday,
  // whose presses are long gone. Both leave the ladder where it was.
  const clean = reading === null || playedClean(reading);
  const countIn = useCountInBeats();

  /**
   * The key just struck for nothing, lit red under the finger that struck it.
   *
   * Phase 2 publishes no hint of its own — the piece is played from memory —
   * so the slot holds nothing but this, and `null` retracts it when the flash
   * is over.
   */
  const strayHint = useMemo<Hint | null>(
    () =>
      run.strays.flashing.length === 0
        ? null
        : {
            source: STRAY_HINT_SOURCE,
            midis: run.strays.flashing.map((midi) => ({
              midi,
              color: JOURNEY_HINT_COLOR_STRAY,
            })),
          },
    [run.strays.flashing],
  );

  useHintPublisher(strayHint);

  // The rail shows this session's traversal when there is one, and the stored
  // one otherwise. Without the second branch a player coming back tomorrow saw
  // an empty rail, as if nothing had ever been played — over a phase that
  // climbs nine rungs across ten to twenty sessions.
  //
  // **Except in the repertoire, where it stops reporting and stays whole.**
  // The FSM allows `repertoire ⇄ phase2`, so a kept piece gets played again,
  // and a single stop would empty a cell — the player would watch their full
  // rail break a few seconds after being told they would have nothing to
  // catch up on. The rail guided the climb; the climb is over. What that
  // traversal saw is not lost, it moves to the one place that offers an act:
  // `toRework` keeps populating, so the passage is still offered.
  const stored = journey.lastTraversal();
  const states = useMemo(
    () =>
      repertoire
        ? cells.map(() => WHOLE)
        : reading !== null
          ? readSections(cells, lastPass?.groups ?? EMPTY_GROUPS, reading)
          : restoreSections(cells, stored?.sections ?? []),
    [cells, lastPass, reading, stored, repertoire],
  );
  // Whether the piece stands crossed, this session or the last one.
  //
  // The stored verdict is read as it was judged **on the day**: recomputing
  // it from the stored shares would let a later change of threshold rewrite
  // what the player was already told. Without it, a piece held yesterday said
  // « Jouer » again this morning, as if nothing had happened.
  //
  // And only at the tempo it was earned at: a piece held at 0.45 says nothing
  // about the rung the player has since climbed to, so the offer must not
  // point upward on its strength. That is what `rate` travels with the map
  // for — without it the stored shares are unreadable in the first place.
  const held =
    reading !== null
      ? heldNow
      : stored !== null && stored.held && stored.rate === rate;
  // Suppressed in the repertoire: "lu il y a 12 jours" is the framing the
  // offer in the empty studio refuses on purpose — the only number on the
  // line, and twelve being larger than seven it reads as a deadline missed.
  // On a piece one keeps, there is no deadline.
  const ago = reading !== null || repertoire ? null : readAgo(stored?.at ?? null, mountedAt);
  const targets = useMemo(
    () =>
      toRework(
        phrases,
        cells,
        lastPass?.groups ?? EMPTY_GROUPS,
        reading,
        (i) => journey.unitRate(i),
        rate,
        bpm,
        ppq,
        MAX_REWORK_LINES,
      ),
    [phrases, cells, lastPass, reading, journey, rate, bpm, ppq],
  );

  // ── The trace ────────────────────────────────────────────────

  /**
   * Where the reading starts.
   *
   * On the stop, one bar early. The playhead sits 100 px off a ~615 px roll,
   * so what is behind it is **16% of the frame**: parking exactly on the stop
   * would put its lead-in under the past gradient, and the lead-in is half of
   * what there is to understand. One bar back puts the stop just above the
   * line, in the clear.
   *
   * On a traversal that held, there is no stop, so it parks on the thinnest
   * cell of the rail — the place the piece was least answered. And when even
   * that is whole, at the beginning: a trace of a clean traversal is not a
   * diagnosis, it is the piece in the player's own hand, and it should be
   * read from the top.
   */
  const traceParkTick = useMemo(() => {
    if (reading === null || range === null) {
      return null;
    }

    const stop = reading.interruptions[0];

    if (stop !== undefined) {
      return Math.max(range.startTick, stop.startTick - ticksPerBar);
    }

    return weakestCellTick(cells, states) ?? range.startTick;
  }, [reading, range, ticksPerBar, cells, states]);

  const trace = useTrace(traceParkTick);
  const traceHide = trace.hide;
  // Never offered on a stored traversal: the lane holds this session's pass
  // and nothing else, so yesterday's rail comes back with no trace behind it.
  const canTrace = lastPass !== null && lastPass.strikes > 0;

  const handleStart = useCallback(() => {
    // Before the pass, not after. `armRunCapture` and the live lane are both
    // emptied at the start of a run, so a trace left open would spend the
    // count-in drawing contours that are about to be deleted.
    traceHide();
    setLastPass(null);
    setJustEntered(false);
    run.clear();
    run.start();
  }, [run, traceHide]);

  const handleOpenWork = useCallback(
    (index: number) => {
      // The work step frames one passage and dims the rest of the roll. A
      // trace of the whole traversal under that veil is 531 contours at 0.42,
      // and none of them is about the passage being opened.
      traceHide();
      setWorking(index);
    },
    [traceHide],
  );

  const handleLeaveWork = useCallback(() => {
    setWorking(null);
  }, []);

  useRunKeyboardShortcuts({
    status: run.status,
    hasFeedback: reading !== null,
    onStart: handleStart,
    onRetry: handleStart,
    onCancel: run.cancel,
  });

  const handsOn = useHandsOnPiano();

  // The piece screen is a hub — a rail, a trace, a work list — so playing
  // launches on the opening group of whatever is selected and never on any
  // key: someone finding a note on this screen is not asking to be sent
  // through the whole piece. The pedal launches outright, as everywhere.
  //
  // Armed only on the hub itself: the work sub-screen is a unit in time and
  // arms its own, and there is nothing to launch behind the trace band.
  useHandsOnControls({
    enabled: run.status !== "running" && working === null && !trace.open,
    onAdvance: handleStart,
    onPlay: handleStart,
    playMidis: groups[0]?.midis ?? null,
  });

  // ── Working one passage ──────────────────────────────────────

  if (working !== null && phrases[working] !== undefined) {
    return (
      <UnitInTime
        phrase={phrases[working]}
        unitIndex={working}
        journey={journey}
        rangeRunner={rangeRunner}
        songControler={songControler}
        bpm={bpm}
        ppq={ppq}
        ticksPerBeat={ticksPerBeat}
        ticksPerBar={ticksPerBar}
        onContinue={handleLeaveWork}
        kicker="À REPRENDRE"
        footer={<BackToPiece onBack={handleLeaveWork} />}
        maxRate={rate}
      />
    );
  }

  if (range === null || cells.length === 0) {
    return null;
  }

  // ── Drawing ──────────────────────────────────────────────────

  const running = run.status === "running";
  const bars = Math.max(1, Math.round((range.endTick - range.startTick) / ticksPerBar));
  const currentBar = barOfTick(run.playedTick ?? range.startTick, range.startTick, ticksPerBar) ?? 1;
  const firstStopBar =
    reading === null || reading.interruptions.length === 0
      ? null
      : barOfTick(reading.interruptions[0].startTick, range.startTick, ticksPerBar);
  // The line names what the button will play, and nothing else. Letting a
  // hover preview a different cell put the kicker and the line on two
  // different parts at once; the cells carry their own `title` and
  // `aria-label`, which is what a hover is for.
  const named = selectedCell;
  const tooLong = seconds > (repertoire ? KEPT_ACT_SECONDS : LONG_ACT_SECONDS) && selectedCell === null;
  const atTop = rate >= RATE_LADDER[RATE_LADDER.length - 1];
  // Never the section's own name: the analyzer labels a section « A » as
  // often as « Refrain », and « Jouer a » is not a sentence. The kicker and
  // the lead line above already say which part is selected.
  // In the repertoire the piece stops being an object with parts, so the
  // action stops naming one: `Jouer · ~2 min`.
  // What the rail's own line says, or nothing. Held to one line so a change
  // of wording never moves the action under the cursor — but removed when
  // there is nothing to say, which in the repertoire is every time.
  const legend: ReactNode =
    // Said first when it is what stands between the player and the next rung,
    // and only then: below the tolerance a stray key changes nothing, and a
    // line about it would be a reproach with no act attached.
    (reading !== null && !clean ? strayLine(reading) : null) ??
    (reading !== null && honoured < 1
      ? "creux : il manquait des notes"
      : (pulseLine(reading?.pulse ?? null, withMetronome) ?? ago));

  // What the panel says above the rail, or nothing at all.
  // Gated on the whole piece: the sentence is about the piece, and it would
  // read as nonsense over a kicker naming one of its parts.
  const lead: ReactNode =
    justEntered && selectedCell === null ? (
      <>
        {/* The only weight this panel's body text ever takes, and it takes it
          exactly once: no tick, no colour, no badge. The palette forbids an
          accent here, and it is that prohibition that makes the weight
          enough. */}
        <span style={{ color: "var(--color-text-primary)", fontWeight: 600 }}>Tu joues ce morceau.</span> On te le
        repropose de temps en temps, pour le garder sous les doigts — tu n’auras rien à rattraper.
      </>
    ) : (
      (traversalLine(reading, honoured, effectiveBpm, firstStopBar) ??
      (repertoire
        ? // A whole rail and a button are a complete screen; the opening line
          // explains a climb that is over.
          null
        : openingLine(
            named,
            tooLong,
            effectiveBpm,
            // Met before counts: a player coming back to a piece they have
            // already crossed does not need to be told again where its tempo
            // comes from.
            reading !== null || stored !== null,
          )))
    );

  const object = selectedCell !== null ? " cette partie" : repertoire ? "" : " le morceau en entier";

  return (
    <FloatingShell
      variant="floating"
      width={PANEL_WIDTH_PX}
      ariaLabel="Le morceau en entier"
      titleId={titleId}
    >
      <FloatingHeader
        kicker={
          selectedCell === null ? "LE MORCEAU EN ENTIER" : `LE MORCEAU · ${sectionName(selectedCell).toUpperCase()}`
        }
        titleId={titleId}
        trailing={<TempoReading bpm={effectiveBpm} rate={rate} />}
      />

      {running ? (
        <RunningBand
          place={selectedCell === null ? null : sectionName(selectedCell)}
          countIn={countIn}
          currentBar={currentBar}
          bars={bars}
          strays={run.strays.count}
          onCancel={run.cancel}
        />
      ) : trace.open ? (
        <TraceBand
          stopBar={firstStopBar}
          hint="Fais défiler le morceau pour la suivre."
          onClose={trace.hide}
        />
      ) : (
        <div style={{ padding: `10px ${PANEL_PADDING_X_PX}px 12px` }}>
          {lead !== null && (
            // Reserved so a one-line verdict and a two-line one do not move
            // the rail under the cursor — but only when there is a verdict.
            // In the repertoire there is none, and a box kept open on nothing
            // is a hole between the header and the rail.
            <div style={{ minHeight: LEAD_BOX_H_PX, marginBottom: 6 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: "var(--color-text-secondary)",
                  textWrap: "pretty",
                }}
              >
                {lead}
              </p>
            </div>
          )}

          <PieceRail
            cells={cells}
            states={states}
            selected={selected}
            onSelect={setSelected}
            stops={repertoire ? EMPTY_STOPS : (reading?.interruptions ?? EMPTY_STOPS)}
            ticksPerBar={ticksPerBar}
            verdict={!repertoire}
          />

          {(legend !== null || canTrace) && (
            <div
              className="flex items-baseline"
              style={{ height: 16, gap: 12, fontSize: 11, color: "var(--color-text-secondary)" }}
            >
              <span className="min-w-0 flex-1 truncate">{legend}</span>

              {canTrace && (
                <button
                  type="button"
                  onClick={trace.show}
                  className={`${OFFER_CLASS} shrink-0`}
                  style={OFFER_INLINE_STYLE}
                >
                  Voir ce que tu as joué
                </button>
              )}
            </div>
          )}

          <PrimaryAction
            label={`${held ? "Rejouer" : "Jouer"}${object} · ${actDuration(seconds)}`}
            onClick={handleStart}
          />

          {handsOn && (
            <HandsOnHint
              items={[
                { gesture: "Joue", effect: "la première note et ça part" },
                { gesture: "Pédale", effect: "lancer" },
              ]}
            />
          )}

          {targets.length > 0 && reading !== null && !held ? (
            <ReworkList targets={targets} phrases={phrases} pieceBpm={effectiveBpm} bpm={bpm} onOpen={handleOpenWork} />
          ) : atTop && held && withMetronome ? (
            <SecondaryOffer label="Jouer sans le métronome" onClick={() => setWithMetronome(false)} />
          ) : held && !clean ? (
            // Crossed, but not under the right keys. `held` is untouched — the
            // traversal happened, the rail says so, and the repertoire still
            // takes the piece. What is withheld is the *rung*, and only that.
            <TempoHeld />
          ) : (
            <TempoOffer rate={rate} faster={held} onStep={handleStepTempo} />
          )}

          <div aria-live="polite" className="sr-only">
            {traversalLine(reading, honoured, effectiveBpm, firstStopBar) ?? ""}
          </div>
        </div>
      )}
    </FloatingShell>
  );
}

type LastPass = {
  readonly reading: RunReading;
  /** The onsets the reading was taken against — a cell's, or the piece's. */
  readonly groups: readonly PracticeGroup[];
  /**
   * How many keys were actually struck during the pass.
   *
   * Read from the live lane at the moment the pass settles, and not derived
   * from the reading: `honoured` counts *answered onsets*, so a player who
   * played twenty wrong notes has zero honoured and twenty strikes. The trace
   * has something to show in that case — it is arguably the case it exists
   * for — and nothing to show when this is zero.
   */
  readonly strikes: number;
};

const EMPTY_GROUPS: readonly PracticeGroup[] = [];

/** What every cell of the rail is worth once the piece is in the repertoire. */
const WHOLE: SectionState = { honoured: 1, stopped: false };
const EMPTY_STOPS: readonly { readonly startTick: number }[] = [];

const EMPTY_RANGE: RunRange = {
  id: "phase2:empty" as PhraseId,
  startTick: 0,
  endTick: 1,
  startMeasure: 1,
  endMeasure: 1,
  notesByHand: { left: [], right: [] },
};

// ── The band shown while the piece runs ───────────────────────────

/**
 * Where the player is, at the scale of the piece.
 *
 * This is the one place where the doctrine of phase 1 is deliberately
 * reversed. In S4, a second progress object next to a scrolling roll is a
 * second clock — true over six seconds. Over six minutes it is false: the
 * roll shows about ten seconds of what is coming, **3% of the traversal**, so
 * it can no longer answer "where am I". The band has to.
 *
 * The hand pills go with it: there is one mode here, so they would read
 * "plays / plays" for six minutes and occupy exactly the room the section
 * name needs.
 *
 * And there is no mm:ss counter. The rail's remaining length **is** the time
 * remaining, because its cells are proportional to duration; a figure ticking
 * every second next to a scrolling roll asks to be watched instead of played.
 * What the duration owes the player is said *before* they commit, in the
 * button.
 */
function RunningBand({
  place,
  countIn,
  currentBar,
  bars,
  strays,
  onCancel,
}: {
  place: string | null;
  countIn: { beat: number; total: number } | null;
  currentBar: number;
  bars: number;
  /** Keys struck for nothing so far in this traversal. */
  strays: number;
  onCancel: () => void;
}) {
  return (
    <div style={{ padding: `10px ${PANEL_PADDING_X_PX}px 12px` }}>
      <div className="flex items-center" style={{ gap: 14 }}>
        <span className="flex-1 truncate" style={{ fontSize: 12, color: "var(--color-text-primary)" }}>
          {countIn !== null ? <CountInDots beat={countIn.beat} total={countIn.total} /> : (place ?? "")}
        </span>

        <span
          className="font-mono tabular-nums shrink-0"
          style={{ fontSize: 12, color: "var(--color-text-secondary)" }}
        >
          mesure {currentBar} / {bars}
        </span>

        {strays > 0 && (
          <span
            className="font-mono tabular-nums shrink-0"
            style={{ fontSize: 12, color: "var(--color-err)" }}
          >
            ✕ {strays}
          </span>
        )}

        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 transition-colors hover:bg-white/[0.10] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
          style={{
            padding: "7px 12px",
            borderRadius: 8,
            border: "1px solid var(--color-border-subtle)",
            background: "rgba(255, 255, 255, 0.06)",
            color: "var(--color-text-primary)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Je m’arrête
        </button>
      </div>

      {/*
        The marker steps by whole bars, with no transition: a 5 px jump every
        few seconds is seen in peripheral vision where a continuous translate
        is not, and a discrete step needs nothing under `prefers-reduced-motion`.
      */}
      <div
        aria-hidden
        style={{
          position: "relative",
          height: 4,
          marginTop: 12,
          borderRadius: 999,
          background: TRACE_EDGE,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: -4,
            left: `${Math.min(100, ((currentBar - 1) / Math.max(1, bars)) * 100)}%`,
            width: 2,
            height: 12,
            borderRadius: 1,
            background: TRACE_MARK,
          }}
        />
      </div>

      {/*
        No live region while it runs. A polite one firing once per bar — 78
        times over a median traversal — drowns the output of someone with both
        hands on the keyboard and nothing to do with it. The position is
        readable on demand instead, and the verdict is announced once, at the
        end, by the panel at rest.
      */}
      <span
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={bars}
        aria-valuenow={currentBar}
        aria-valuetext={`mesure ${currentBar} sur ${bars}${place === null ? "" : `, ${place}`}`}
        aria-live="off"
        className="sr-only"
      />
    </div>
  );
}

/**
 * What the panel becomes while the trace is up.
 *
 * It folds to the running band's geometry, and that is not a nicety. The
 * panel is 560 px plus a 16 px inset on a roll around 1380 px — **42% of it**,
 * and the right-hand third at that, which is where the notes cluster around
 * middle C. A trace read through that is a trace read on the half of the
 * piece that happened to fall outside the panel.
 *
 * The second line is the instruction, and it is the only one this screen ever
 * gives about the roll: the transport is parked, so nothing will scroll on its
 * own, and a player who does not know the roll can be dragged sees one frozen
 * frame and concludes the feature is broken.
 */
function CountInDots({ beat, total }: { beat: number; total: number }) {
  return (
    <span className="flex items-center" style={{ gap: 6 }}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: i <= beat ? TRACE_MARK : TRACE_EDGE,
          }}
        />
      ))}
      <span className="sr-only">Décompte</span>
    </span>
  );
}

// ── The pieces of the panel ───────────────────────────────────────

/**
 * The tempo, and how far up the ladder it stands.
 *
 * The climb is **9 rungs in the median** — 49 BPM to 106 — spread over ten to
 * twenty sessions, and its only witness was a number that changed. Eleven
 * segments cost eight pixels in a header that has forty-two, and none in the
 * body.
 *
 * `aria-hidden`, no pointer, no hover: the BPM beside it says everything, and
 * anything that looks like a slider on this screen would invite fiddling with
 * the one value the player should only move a rung at a time.
 */
function TempoReading({ bpm, rate }: { bpm: number; rate: number }) {
  const nearest = RATE_LADDER.reduce((best, r) => (Math.abs(r - rate) < Math.abs(best - rate) ? r : best));
  const rung = RATE_LADDER.indexOf(nearest);

  return (
    <span className="flex shrink-0 items-center" style={{ gap: 8 }}>
      <span aria-hidden className="flex items-end" style={{ gap: 2 }}>
        {RATE_LADDER.map((_, i) => (
          <span
            key={i}
            style={{
              width: 4,
              height: 3,
              borderRadius: 1,
              background: i === rung ? TRACE_MARK : i < rung ? TRACE_INK : "rgba(255, 255, 255, 0.10)",
            }}
          />
        ))}
      </span>

      <span className="font-mono tabular-nums" style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
        {bpm} BPM
      </span>
    </span>
  );
}

/**
 * The primary action carries its own duration.
 *
 * This is the only screen of the product that asks for a commitment of more
 * than ten minutes — **6 min 12 in the median, 13 min 59 at the p90** — and
 * it used to say « Lancer un run ».
 */
function PrimaryAction({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "11px 14px",
        borderRadius: 8,
        border: "none",
        background: "#ffffff",
        color: "var(--color-bg-deep, #0e0e14)",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      ▶ {label}
    </button>
  );
}

/**
 * The work list — and the thing about it that is not a copy decision.
 *
 * `run-controls.tsx` states the invariant phase 2 was breaking: *"Playing it
 * again is the primary action […] the whole point of a short unit is that it
 * can be repeated four times in two or three minutes."* Replaying is primary
 * **because a unit lasts six seconds**. At six minutes, a white "again"
 * button asks for twelve more. So after a traversal that did not hold, the
 * primary action is not another traversal — it is the first line of this
 * list, and that is where the whole shift of the phase is expressed.
 *
 * The tempo on the right appears only when it differs from the piece's: it is
 * the fact that decides the click — this passage is slower than the piece —
 * and the silence rule covers the rest.
 */
function ReworkList({
  targets,
  phrases,
  pieceBpm,
  bpm,
  onOpen,
}: {
  targets: readonly ReworkTarget[];
  phrases: readonly Phrase[];
  pieceBpm: number;
  bpm: number;
  onOpen: (index: number) => void;
}) {
  return (
    <div style={{ marginTop: 12 }}>
      <div
        className="font-mono uppercase"
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: "0.18em",
          color: "var(--color-text-secondary)",
          marginBottom: 6,
        }}
      >
        À reprendre
      </div>

      <div style={{ display: "grid", gap: 4 }}>
        {targets.map((target, index) => {
          // Never above the piece's own tempo: a passage is taken up again
          // because the piece does not hold there, so working it faster than
          // the piece would train the wrong speed.
          const unitBpm = Math.min(bpmAt(bpm, target.rate), pieceBpm);
          const primary = index === 0;

          return (
            <button
              key={target.index}
              type="button"
              onClick={() => onOpen(target.index)}
              className="flex w-full items-center transition-colors hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
              style={{
                gap: 12,
                padding: "7px 10px",
                marginLeft: -10,
                width: "calc(100% + 20px)",
                borderRadius: 8,
                border: "none",
                background: primary ? "#ffffff" : "transparent",
                color: primary ? "var(--color-bg-deep, #0e0e14)" : "var(--color-text-primary)",
                fontSize: 12,
                fontWeight: primary ? 600 : 400,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span className="flex-1 truncate">{reworkLabel(target, phrases[target.index])}</span>

              {unitBpm !== pieceBpm && (
                <span
                  className="font-mono tabular-nums shrink-0"
                  style={{
                    fontSize: 11,
                    opacity: primary ? 0.7 : 1,
                    color: primary ? "inherit" : "var(--color-text-secondary)",
                  }}
                >
                  {unitBpm} BPM
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The offer that replaces the tempo once there is no rung left.
 *
 * The click is the first of the four aids to go, and the only one this lot
 * takes: the roll stays, because it is not an assistance but the notation —
 * removing it would not make the player play unaided, it would make them play
 * from memory, which is a different skill and not this product's.
 */
function SecondaryOffer({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={OFFER_CLASS}
      style={{ ...OFFER_STYLE, marginTop: 12 }}
    >
      {label}
    </button>
  );
}

/**
 * One offer, pointing the way the situation does.
 *
 * A `−`/`+` stepper makes the tempo the subject of the screen — a control
 * with two buttons asks to be turned — and it is symmetric where the
 * situation is not: before holding the piece, only slowing down is a
 * legitimate move; after, only speeding up.
 */
/**
 * Why the rung is not on offer. Same sentence as `S4` and `S5`, one object
 * wider — a player meets this line at three different steps and it must be
 * recognisably the same rule each time.
 */
function TempoHeld() {
  return (
    <span
      style={{
        display: "inline-block",
        marginTop: 10,
        fontSize: 11,
        color: "var(--color-text-secondary)",
      }}
    >
      Le tempo monte quand le morceau sort juste.
    </span>
  );
}

function TempoOffer({ rate, faster, onStep }: { rate: number; faster: boolean; onStep: (direction: -1 | 1) => void }) {
  const atEnd = faster ? rate >= RATE_LADDER[RATE_LADDER.length - 1] : rate <= RATE_LADDER[0];

  if (atEnd) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => onStep(faster ? 1 : -1)}
      className={OFFER_CLASS}
      style={{ ...OFFER_STYLE, marginTop: 12 }}
    >
      {faster ? "Un peu plus vite" : "Un peu plus lentement"}
    </button>
  );
}

/**
 * The way back, in the slot the stepper left free.
 *
 * Same height, same place, same family of word: where a step counter sat in
 * phase 1 is where "back" belongs in phase 2.
 */
function BackToPiece({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex items-center px-4 py-3" style={{ borderTop: "1px solid var(--color-border-subtle)" }}>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center rounded-md px-1.5 py-0.5 transition-colors hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
        style={{
          gap: 6,
          border: "none",
          background: "transparent",
          color: "var(--color-text-secondary)",
          fontSize: 11,
          cursor: "pointer",
        }}
      >
        <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>
          ‹
        </span>
        Revenir au morceau
      </button>
    </div>
  );
}

// ── Copy ──────────────────────────────────────────────────────────

/**
 * What the screen says when there is no traversal to report.
 *
 * The tempo of the piece is stated **once**, on arrival: it is the fact that
 * explains why the piece is slower than it is written, and it is noise every
 * time after. Pointing at a cell names it instead — the rail carries no
 * labels of its own, so the line is where a section gets its name.
 */
function openingLine(named: PieceSection | null, tooLong: boolean, effectiveBpm: number, played: boolean): string {
  if (named !== null) {
    return `${sectionName(named)}, mesures ${named.startMeasure} à ${named.endMeasure}.`;
  }

  if (!played) {
    return `Toutes les phrases y sont. On joue le morceau au tempo qu’il tient partout : ${effectiveBpm} BPM.`;
  }

  if (tooLong) {
    return "Le morceau est long à ce tempo. Tu peux le prendre partie par partie.";
  }

  return "Va au bout. Si tu rates une note, tu continues.";
}

// ── Helpers ───────────────────────────────────────────────────────

/**
 * The span an act covers: one cell of the rail, or the whole piece.
 *
 * A `RunRange` and not a `Phrase`. The old full-song object was typed as a
 * phrase and therefore carried an id, a section, an index and grips that
 * belonged to nothing — four lying fields out of eleven, which is exactly
 * what `RunRange` was extracted to stop.
 */
function spanRange(phrases: readonly Phrase[], cell: PieceSection | null): RunRange | null {
  if (phrases.length === 0) {
    return null;
  }

  const indices = cell === null ? phrases.map((_, i) => i) : [...cell.unitIndices];

  if (indices.length === 0) {
    return null;
  }

  const first = phrases[indices[0]];
  const last = phrases[indices[indices.length - 1]];

  return {
    id: `phase2:${cell?.id ?? "all"}` as PhraseId,
    startTick: first.startTick,
    endTick: last.endTick,
    startMeasure: first.startMeasure,
    endMeasure: last.endMeasure,
    notesByHand: {
      left: indices.flatMap((i) => phrases[i].notesByHand.left),
      right: indices.flatMap((i) => phrases[i].notesByHand.right),
    },
  };
}
